import { Fragment, memo, useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiExpandableBox,
  MiIcon,
  MiStackedTypography,
} from "@miplatform/components";
import { WorkOrderStatus } from "@miplatform/types";
import { getNestedValue, parseFieldValue } from "@miplatform/utils";
import { mdiFileDocumentOutline } from "@mdi/js";
import { Box, GridLegacy, Paper, Typography } from "@mui/material";
import { OrderInfoCardEmails } from "./OrderInfoCardEmails/OrderInfoCardEmails";
import { WorkOrderCard } from "./WorkOrderCard/WorkOrderCard";

/**
 * A component that displays detailed information about an order in a card format.
 *
 * @param {Object} props - Component props.
 * @param {Object} props.order - The order object containing information to be displayed.
 * @param {Object} props.order.home - The home object associated with the order.
 * @param {string} props.order.orderTemplateName - The name of the product associated with the order.
 * @param {string} props.order.orderStatus - The status of the order.
 * @param {string} props.order.notes - Additional notes about the order.
 * @param {Object} props.fieldsConfig - The config object containing fields to be displayed.
 *
 * @returns {JSX.Element} The rendered order information card.
 *
 * @example
 * <OrderInfoCard
 *   order={{
 *     home: {
 *       address: { city: "New York", regionCode: "NY", addressLine1: "123 Main St" },
 *       communityName: "Sunset Valley",
 *       builderName: "ABC Builders",
 *       waterHeaterType: "Electric",
 *       complexity: "3",
 *     },
 *     orderTemplateName: "Water Heater",
 *     orderStatus: "Completed",
 *     notes: "Installation completed successfully.",
 *   }}
 * />
 */
export const OrderInfoCard = memo(
  ({ order, fieldsConfig, hideEmails, onClose, holdType }) => {
    const {
      home,
      orderTemplateName,
      orderStatus,
      workOrders = [],
      notes,
    } = order;
    const { theme, classes } = useStyles({ order });
    const homeAddressFields = [
      home?.address?.city,
      home?.address?.regionCode,
      home?.communityName,
      home?.builderName,
    ];
    const completedWorkOrdersCount = useMemo(
      () =>
        workOrders.filter(({ status }) => status === WorkOrderStatus.Complete)
          .length,
      [workOrders]
    );

    const renderFields = () =>
      fieldsConfig?.map(({ label, fieldKey }) => (
        <GridLegacy item key={fieldKey} xs={6}>
          <MiStackedTypography
            title={label}
            description={parseFieldValue(getNestedValue(order, fieldKey))}
          />
        </GridLegacy>
      ));

    const renderHomeInfo = () =>
      homeAddressFields.filter(Boolean).map((field, i) => (
        <Fragment key={field}>
          {field}
          {i !== homeAddressFields.length - 1 && " | "}
        </Fragment>
      ));

    return (
      <Paper className={classes.paper} onClick={(e) => e.stopPropagation()}>
        <Box className={classes.fixedHeader}>
          <Box className={classes.header}>
            <Typography className={classes.headerText}>
              {orderTemplateName}
            </Typography>
            <Typography className={classes.headerText}>
              {orderStatus}
            </Typography>
          </Box>
          <Box className={classes.topContent}>
            <Typography className={classes.addressText}>
              {home?.address?.addressLine1}
            </Typography>
            <Typography className={classes.fieldText}>
              {renderHomeInfo()}
            </Typography>
          </Box>
        </Box>
        <Box className={classes.scrollableContent}>
          {fieldsConfig && fieldsConfig.length ? (
            <GridLegacy container className={classes.middleContent}>
              {renderFields()}
            </GridLegacy>
          ) : null}
          {workOrders.length !== 0 && (
            <MiExpandableBox
              defaultExpanded
              title="Work Orders"
              sx={{ pt: 2 }}
              rightHeaderContent={
                <Box display="flex" alignItems="center" gap={1}>
                  <MiIcon
                    path={mdiFileDocumentOutline}
                    size={0.6}
                    color={theme.palette.warning.main}
                    style={{
                      marginBottom: theme.spacing(0.5),
                    }}
                  />
                  <Typography
                    fontSize={theme.fontSize.small}
                    fontWeight={theme.fontWeight.semiBold}
                  >
                    {completedWorkOrdersCount}/{workOrders.length}
                  </Typography>
                </Box>
              }
            >
              <Box display="flex" flexDirection="column" gap={1}>
                {workOrders.map((workOrder) => (
                  <WorkOrderCard key={workOrder.id} workOrder={workOrder} />
                ))}
              </Box>
            </MiExpandableBox>
          )}
          {!hideEmails && (
            <OrderInfoCardEmails order={order} onClose={onClose} />
          )}
          <MiStackedTypography
            className={classes.bottomContent}
            title="Order Notes"
            description={notes || "--"}
            descriptionProps={{
              style: {
                whiteSpace: "initial",
              },
            }}
          />
          <MiStackedTypography
            className={classes.bottomContent}
            title="Hold Type"
            description={holdType || "--"}
            descriptionProps={{
              style: {
                whiteSpace: "initial",
              },
            }}
          />
        </Box>
      </Paper>
    );
  }
);

const useStyles = makeStyles()((theme, { order = {} }) => ({
  paper: {
    position: "relative",
    borderRadius: theme.spacing(1.5),
    width: 374,
    border: `1px solid ${
      order?.orderTemplateColor || theme.palette.primary.main
    }`,

    maxHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },

  fixedHeader: {
    flexShrink: 0,
  },
  scrollableContent: {
    flex: 1,
    overflowY: "auto",
  },

  header: {
    backgroundColor: order?.orderTemplateColor || theme.palette.primary.main,
    padding: theme.spacing(1, 2, 1, 2),
    display: "flex",
    justifyContent: "space-between",
    maxWidth: "100%",
    position: "relative",
  },
  headerText: {
    fontSize: theme.fontSize.small,
    color: theme.palette.primary.contrastText,
  },
  topContent: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
  addressText: {
    fontSize: theme.fontSize.medium,
    weight: theme.fontWeight.semiBold,
  },
  fieldText: {
    display: "flex",
    fontSize: theme.fontSize.small,
    weight: theme.fontWeight.regular,
    color: theme.palette.grey.main,
  },
  middleContent: {
    spacing: theme.spacing(2),
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.grey["100"],
  },
  bottomContent: {
    padding: theme.spacing(2),
    maxWidth: "100%",
  },
}));
